import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../../utils/axiosConfig';
import './VehicleForm.css';

const validationSchema = Yup.object().shape({
    vehicle_id: Yup.string()
        .required('Vehicle ID is required')
        .matches(/^[a-z0-9-]+$/, 'Vehicle ID can only contain lowercase letters, numbers, and hyphens'),
    slug: Yup.string()
        .required('Slug is required')
        .matches(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
    title_en: Yup.string().required('Title (EN) is required'),
    title_ar: Yup.string().required('Title (AR) is required'),
    category: Yup.string().required('Category is required'),
    short_description_en: Yup.string(),
    short_description_ar: Yup.string(),
});

const VehicleForm = ({ vehicleId = null, isEditMode = false }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            vehicle_id: '',
            slug: '',
            title_en: '',
            title_ar: '',
            category: '',
            discontinued: false,
            show_on_spare_parts_page: true,
            maintenance_only: false,
            coming_soon: false,
            short_description_en: '',
            short_description_ar: '',
            main_image: '',
            video_url: '',
            brochure_url: '',
            sort_order: 0,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                if (isEditMode) {
                    await axiosInstance.put(`/vehicles/${vehicleId}`, values);
                    toast.success('Vehicle updated successfully!');
                } else {
                    await axiosInstance.post('/vehicles', values);
                    toast.success('Vehicle created successfully!');
                    setTimeout(() => navigate('/vehicle-management'), 1500);
                }
            } catch (error) {
                console.error('Error saving vehicle:', error);
                const errorMessage = error.response?.data?.message ||
                    error.response?.data?.errors ?
                        Object.values(error.response.data.errors).flat().join(', ') :
                        'Failed to save vehicle';
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        }
    });

    useEffect(() => {
        if (isEditMode && vehicleId) {
            const fetchVehicle = async () => {
                try {
                    const response = await axiosInstance.get(`/vehicles/${vehicleId}`);
                    const vehicle = response.data;
                    formik.setValues({
                        vehicle_id: vehicle.vehicle_id || '',
                        slug: vehicle.slug || '',
                        title_en: vehicle.title_en || '',
                        title_ar: vehicle.title_ar || '',
                        category: vehicle.category || '',
                        discontinued: vehicle.discontinued || false,
                        show_on_spare_parts_page: vehicle.show_on_spare_parts_page ?? true,
                        maintenance_only: vehicle.maintenance_only || false,
                        coming_soon: vehicle.coming_soon || false,
                        short_description_en: vehicle.short_description_en || '',
                        short_description_ar: vehicle.short_description_ar || '',
                        main_image: vehicle.main_image || '',
                        video_url: vehicle.video_url || '',
                        brochure_url: vehicle.brochure_url || '',
                        sort_order: vehicle.sort_order || 0,
                    });
                } catch (error) {
                    console.error('Error fetching vehicle:', error);
                    toast.error('Failed to load vehicle data');
                }
            };
            fetchVehicle();
        }
    }, [isEditMode, vehicleId]);

    const handleTitleChange = (e) => {
        const title = e.target.value;
        formik.setFieldValue('title_en', title);
        if (!isEditMode && !formik.touched.vehicle_id) {
            const generatedId = title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            formik.setFieldValue('vehicle_id', generatedId);
            formik.setFieldValue('slug', generatedId);
        }
    };

    return (
        <div className="vehicle-form">
            <ToastContainer />
            <Form onSubmit={formik.handleSubmit}>
                {/* Basic Information */}
                <Row className="border-bottom pb-4 mb-4">
                    <Col md={4}>
                        <h5>Basic Information</h5>
                        <p className="text-muted">Core vehicle details</p>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Vehicle ID <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="vehicle_id"
                                        value={formik.values.vehicle_id}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={formik.touched.vehicle_id && !!formik.errors.vehicle_id}
                                        disabled={isEditMode}
                                        placeholder="e.g., dzire, baleno"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.vehicle_id}
                                    </Form.Control.Feedback>
                                    <Form.Text className="text-muted">
                                        Unique identifier (lowercase, hyphens allowed)
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>URL Slug <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="slug"
                                        value={formik.values.slug}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={formik.touched.slug && !!formik.errors.slug}
                                        placeholder="e.g., dzire"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.slug}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Title (English) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title_en"
                                        value={formik.values.title_en}
                                        onChange={handleTitleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={formik.touched.title_en && !!formik.errors.title_en}
                                        placeholder="e.g., Dzire"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.title_en}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Title (Arabic) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title_ar"
                                        value={formik.values.title_ar}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={formik.touched.title_ar && !!formik.errors.title_ar}
                                        dir="rtl"
                                        placeholder="العنوان بالعربي"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.title_ar}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Category <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        name="category"
                                        value={formik.values.category}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={formik.touched.category && !!formik.errors.category}
                                    >
                                        <option value="">Select Category</option>
                                        <option value="Sedan">Sedan</option>
                                        <option value="Hatchback">Hatchback</option>
                                        <option value="SUV/Crossover">SUV/Crossover</option>
                                        <option value="Commercial">Commercial</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.category}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Sort Order</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="sort_order"
                                        value={formik.values.sort_order}
                                        onChange={formik.handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                {/* Description */}
                <Row className="border-bottom pb-4 mb-4">
                    <Col md={4}>
                        <h5>Description</h5>
                        <p className="text-muted">Vehicle descriptions in both languages</p>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Short Description (English)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        name="short_description_en"
                                        value={formik.values.short_description_en}
                                        onChange={formik.handleChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Short Description (Arabic)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        name="short_description_ar"
                                        value={formik.values.short_description_ar}
                                        onChange={formik.handleChange}
                                        dir="rtl"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                {/* Media URLs */}
                <Row className="border-bottom pb-4 mb-4">
                    <Col md={4}>
                        <h5>Media</h5>
                        <p className="text-muted">Images and media links</p>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Main Image URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="main_image"
                                        value={formik.values.main_image}
                                        onChange={formik.handleChange}
                                        placeholder="https://..."
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Video URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="video_url"
                                        value={formik.values.video_url}
                                        onChange={formik.handleChange}
                                        placeholder="https://youtube.com/..."
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Brochure URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="brochure_url"
                                        value={formik.values.brochure_url}
                                        onChange={formik.handleChange}
                                        placeholder="https://..."
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                {/* Flags */}
                <Row className="border-bottom pb-4 mb-4">
                    <Col md={4}>
                        <h5>Status Flags</h5>
                        <p className="text-muted">Vehicle visibility settings</p>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={6}>
                                <Form.Check
                                    type="switch"
                                    id="discontinued"
                                    name="discontinued"
                                    label="Discontinued"
                                    checked={formik.values.discontinued}
                                    onChange={formik.handleChange}
                                    className="mb-3"
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Check
                                    type="switch"
                                    id="coming_soon"
                                    name="coming_soon"
                                    label="Coming Soon"
                                    checked={formik.values.coming_soon}
                                    onChange={formik.handleChange}
                                    className="mb-3"
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Check
                                    type="switch"
                                    id="show_on_spare_parts_page"
                                    name="show_on_spare_parts_page"
                                    label="Show on Spare Parts Page"
                                    checked={formik.values.show_on_spare_parts_page}
                                    onChange={formik.handleChange}
                                    className="mb-3"
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Check
                                    type="switch"
                                    id="maintenance_only"
                                    name="maintenance_only"
                                    label="Maintenance Only"
                                    checked={formik.values.maintenance_only}
                                    onChange={formik.handleChange}
                                    className="mb-3"
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>

                {/* Submit */}
                <div className="d-flex justify-content-between">
                    <Button variant="outline-secondary" onClick={() => navigate('/vehicle-management')}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? 'Saving...' : (isEditMode ? 'Update Vehicle' : 'Create Vehicle')}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default VehicleForm;
